import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { Feature, Polygon } from 'geojson';

import { useT } from '../i18n';
import { computeAreaSqm, computeCentroidLatLng, formatHa, formatSqm } from '../lib/geo';
import { reverseGeocode, type GeocodeResult } from '../lib/nominatim';
import { JUDETE, findJudetCode } from '../domain/judete';
import type { ParcelDetails, ParcelSelection } from '../domain/types';
import { parseDecimal, type Errors } from '../lib/validation';
import { Field } from './Field';
import { LocalitySearch } from './LocalitySearch';
import { ParcelMap, type MapMode, type ParcelMapHandle } from './ParcelMap';
import './StepLocate.css';

interface StepLocateProps {
  selection: ParcelSelection;
  errors: Errors;
  onSelectionChange: (next: ParcelSelection) => void;
  /** Owned by App: which location fields we filled. Survives leaving this step. */
  autoFilledRef: MutableRefObject<{ judet: boolean; localitate: boolean }>;
  /** The location answers live on this step now, so it edits them directly. */
  details: ParcelDetails;
  onDetailsChange: (update: Partial<ParcelDetails>) => void;
}

export function StepLocate({
  selection,
  errors,
  onSelectionChange,
  autoFilledRef,
  details,
  onDetailsChange,
}: StepLocateProps) {
  const t = useT();
  const mapRef = useRef<ParcelMapHandle | null>(null);

  const [mode, setMode] = useState<MapMode>('idle');

  /*
   * Which route is open is not local state: the toggle that switches between
   * them now sits in the wizard's nav row, beside Continue, so the selection's
   * own method is the single source of truth for both.
   */
  const manualOpen = selection.method === 'manual';

  // Guards the reverse geocode so it runs once per drawn parcel, not on every
  // vertex nudge — Nominatim is rate-limited and this is only a convenience.
  const geocodedRef = useRef(false);

  const onSelectionChangeRef = useRef(onSelectionChange);
  const onDetailsChangeRef = useRef(onDetailsChange);
  const detailsRef = useRef(details);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
    onDetailsChangeRef.current = onDetailsChange;
    detailsRef.current = details;
  });

  const handlePolygonChange = useCallback((polygon: Feature<Polygon> | null) => {
    if (!polygon) {
      geocodedRef.current = false;
      onSelectionChangeRef.current({
        method: 'draw',
        geojson: null,
        areaSqm: null,
        centroid: null,
        localityGuess: '',
      });
      return;
    }

    const areaSqm = computeAreaSqm(polygon);
    const centroid = computeCentroidLatLng(polygon);

    onSelectionChangeRef.current({
      method: 'draw',
      geojson: polygon,
      areaSqm,
      centroid,
      localityGuess: '',
    });

    if (!geocodedRef.current) {
      geocodedRef.current = true;
      void reverseGeocode(centroid[0], centroid[1]).then((result) => {
        if (!result) return;
        onSelectionChangeRef.current({
          method: 'draw',
          geojson: polygon,
          areaSqm,
          centroid,
          localityGuess: result.locality,
        });
        // Fill the location here rather than on leaving the step: the fields are on THIS
        // step now, and the owner should see what was resolved while the map is in view.
        //
        // Overwrite what WE filled; never overwrite what the owner typed. The earlier
        // version only wrote into empty fields, which meant redrawing the parcel
        // somewhere else left the first parcel's county and locality sitting there —
        // silently attaching the wrong place to the new outline.
        const auto = autoFilledRef.current;
        const code = findJudetCode(result.judetRaw);
        if (code && (detailsRef.current.judet === '' || auto.judet)) {
          onDetailsChangeRef.current({ judet: code });
          auto.judet = true;
        }
        if (detailsRef.current.localitate.trim() === '' || auto.localitate) {
          onDetailsChangeRef.current({ localitate: result.locality });
          auto.localitate = true;
        }
      });
    }
  }, []);

  const handleSearchSelect = useCallback((result: GeocodeResult) => {
    mapRef.current?.flyToResult(result);
  }, []);

  /**
   * A picked address on the manual route fills the county and locality below it.
   *
   * The manual route has no map to reverse-geocode, so before this the owner had to
   * type where the land is and then say it again in two more fields. The geocoder
   * already knows both from the suggestion they chose; asking again was the duplication.
   *
   * Parsed off the result label, whose last segment is the county and whose first is
   * the place. Replaces what we filled before, never what the owner typed.
   */
  const handleManualPick = useCallback((result: GeocodeResult) => {
    const parts = result.label
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return;

    for (const part of [...parts].reverse()) {
      const code = findJudetCode(part);
      if (code) {
        if (detailsRef.current.judet === '' || autoFilledRef.current.judet) {
          onDetailsChangeRef.current({ judet: code });
          autoFilledRef.current.judet = true;
        }
        break;
      }
    }
    if (detailsRef.current.localitate.trim() === '' || autoFilledRef.current.localitate) {
      onDetailsChangeRef.current({ localitate: parts[0]! });
      autoFilledRef.current.localitate = true;
    }
  }, []);

  const hasPolygon = selection.method === 'draw' && selection.geojson !== null;
  const areaSqm = selection.method === 'draw' ? selection.areaSqm : null;

  const hint =
    mode === 'drawing'
      ? t.step1.drawHintDrawing
      : hasPolygon
        ? t.step1.drawHintDone
        : t.step1.drawHintIdle;

  return (
    <div className="step-locate">
      <header className="step-locate__intro">
        <h2>{t.step1.heading}</h2>
        <p className="lede">{t.step1.lede}</p>
      </header>

      {!manualOpen && (
        <>
          <div className="step-locate__search">
            <LocalitySearch onSelect={handleSearchSelect} />
          </div>

          {/*
            The map and the instrument bar are one glass panel: a single border,
            radius and drop shadow wrap the pair, and they read as one object.
            The bar sits beneath the map rather than floating over it — on a
            phone, controls over the canvas cover the land being drawn.
          */}
          <div className="map-panel">
            <div className="step-locate__map">
              {/* Seeded so returning from step 2 restores the drawn outline. */}
              <ParcelMap
                ref={mapRef}
                initialPolygon={selection.method === 'draw' ? selection.geojson : null}
                onPolygonChange={handlePolygonChange}
                onModeChange={setMode}
              />
            </div>

            {/*
              The readout is permanent — the area is what this step exists to
              produce, so its label holds its place on the bar from the first
              paint, before there is a figure to put under it.
            */}
            <div className="instrument-bar">
            <div className="instrument-bar__readout">
              <span className="eyebrow">{t.step1.areaLabel}</span>
              {areaSqm === null ? (
                <span className="instrument-bar__empty">{t.step1.areaEmpty}</span>
              ) : (
                <span className="instrument-bar__value">
                  {t.step1.areaSqm(formatSqm(areaSqm))}
                  <span className="instrument-bar__secondary">{t.step1.areaHa(formatHa(areaSqm))}</span>
                </span>
              )}
            </div>

            {/*
              One control, three states. "Start over" both clears and re-enters
              draw mode, because clearing and then hunting for "Draw" again is
              two taps for one intent.
            */}
            <div className="instrument-bar__controls">
              {mode === 'drawing' ? (
                <button type="button" className="btn btn--quiet" onClick={() => mapRef.current?.stopDraw()}>
                  {t.step1.drawStop}
                </button>
              ) : (
                <button
                  type="button"
                  className={hasPolygon ? 'btn btn--quiet' : 'btn btn--accent'}
                  onClick={() => mapRef.current?.startDraw()}
                >
                  {hasPolygon ? t.step1.drawReset : t.step1.drawStart}
                </button>
              )}
            </div>
          </div>
          </div>

          {/* Live region: the area is the one number that changes without the
              owner touching a control, so it must be announced. */}
          <span className="visually-hidden" role="status" aria-live="polite">
            {areaSqm === null ? '' : t.step1.areaSqm(formatSqm(areaSqm))}
          </span>

          {/* What to do next, directly under the panel it applies to. */}
          <p className="step-locate__hint">{hint}</p>

          {errors['parcel'] && (
            <p className="field__error" role="alert">
              <span className="visually-hidden">{t.validation.fieldError}</span>
              <span aria-hidden="true">↳</span>
              {errors['parcel']}
            </p>
          )}
        </>
      )}

      {manualOpen && (
        <ManualFallback selection={selection} errors={errors} onSelectionChange={onSelectionChange} onPick={handleManualPick} />
      )}

      {/*
        Where the parcel is belongs to the step about where the parcel is. These used to
        sit in step 2 among the urbanism questions, which asked the owner to say twice
        what they had just pinned on a map.

        Shown once there is something to confirm: an outline exists, or the manual route
        is open. Both values are prefilled — the drawn route from the centroid
        reverse-geocode, the manual route from the address as it is typed — and both stay
        editable, because the reverse-geocode names a commune correctly far more often
        than it names the village inside it.
      */}
      {(hasPolygon || manualOpen) && (
        <section className="step-locate__where" aria-label={t.step1.whereHeading}>
          <div className="step-locate__where-fields">
            <Field name="judet" label={t.step1.judetLabel} help={t.step1.whereLede} error={errors['judet']}>
              {(props) => (
                <select
                  {...props}
                  className="select"
                  value={details.judet}
                  onChange={(e) => {
                    autoFilledRef.current.judet = false;
                    onDetailsChange({ judet: e.target.value });
                  }}
                >
                  <option value="">{t.step1.judetPlaceholder}</option>
                  {JUDETE.map((j) => (
                    <option key={j.code} value={j.code}>
                      {j.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field
              name="localitate"
              label={t.step1.localitateLabel}
              help={t.step1.localitateHelp}
              error={errors['localitate']}
            >
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="text"
                  autoComplete="address-level2"
                  placeholder={t.step1.localitatePlaceholder}
                  value={details.localitate}
                  onChange={(e) => {
                    autoFilledRef.current.localitate = false;
                    onDetailsChange({ localitate: e.target.value });
                  }}
                />
              )}
            </Field>
          </div>
        </section>
      )}
    </div>
  );
}

interface ManualFallbackProps {
  selection: ParcelSelection;
  errors: Errors;
  onSelectionChange: (next: ParcelSelection) => void;
  /** A picked suggestion fills the county and locality below. */
  onPick: (result: GeocodeResult) => void;
}

function ManualFallback({ selection, errors, onSelectionChange, onPick }: ManualFallbackProps) {
  const t = useT();
  const areaValue = selection.areaSqm === null ? '' : String(selection.areaSqm);

  return (
    <section className="manual-fallback" aria-labelledby="manual-fallback-heading">
      <h3 id="manual-fallback-heading">{t.step1.fallbackHeading}</h3>
      <p className="field__help">{t.step1.fallbackLede}</p>

      <div className="manual-fallback__fields">
        <Field name="manualArea" label={t.step1.fallbackAreaLabel} error={errors['manualArea']}>
          {(props) => (
            <input
              {...props}
              className="input"
              // type=text, not number: a number input silently discards what it
              // cannot parse and hides typos behind an empty box.
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder={t.step1.fallbackAreaPlaceholder}
              value={areaValue}
              onChange={(e) => {
                const parsed = parseDecimal(e.target.value);
                onSelectionChange({ ...selection, method: 'manual', areaSqm: parsed });
              }}
            />
          )}
        </Field>

        {/*
          The same fuzzy picker the map uses, not a plain box. This is the one field on
          the manual route where the owner types where the land is, so it is exactly
          where typo tolerance earns its keep: "Dulcanca" and "Otopenii" resolve here
          and match nothing at all in an exact search.

          Picking a suggestion also fills the county and locality below, which is why
          those two stopped being a second version of this question.
        */}
        <div className="manual-fallback__address">
          <LocalitySearch
            label={t.step1.fallbackLocalityLabel}
            placeholder={t.step1.fallbackLocalityPlaceholder}
            initialQuery={selection.localityGuess}
            onQueryChange={(value) =>
              onSelectionChange({ ...selection, method: 'manual', localityGuess: value })
            }
            onSelect={onPick}
          />
          {errors['manualLocality'] && (
            <p className="field__error" role="alert">
              <span aria-hidden="true">↳</span>
              {errors['manualLocality']}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
